import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { OrderGroupDetail } from "@/api/models/api-types";
import { formatPrice, formatCredit } from "../utils/format-price";

interface OrderSummarySectionProps {
  orderGroupDetail?: OrderGroupDetail;
  currency: string;
}

export const OrderSummarySection: React.FC<OrderSummarySectionProps> = ({
  orderGroupDetail,
  currency,
}) => {
  const { t } = useTranslation();

  const totalAmount = orderGroupDetail?.totalAmount ?? 0;
  const shipping = orderGroupDetail?.shippingCost ?? 0;
  const subtotal = orderGroupDetail?.subTotal ?? totalAmount - shipping;
  const cashback = orderGroupDetail?.cashbackAmount ?? 0;

  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-0 px-4 md:px-6 py-4 md:py-6">
      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary">
        {t("orders.summary")}
      </h3>
      <div className="flex flex-col gap-2 w-full md:w-auto md:text-right">
        {/* Price Breakdown */}
        <div className="space-y-2">
          <div className="flex justify-between items-center gap-4 md:gap-8">
            <span className="text-sm font-medium text-text-primary dark:text-text-primary">
              {t("orders.subtotal")}
            </span>
            <span className="text-sm font-semibold text-text-primary dark:text-text-primary">
              {formatPrice(subtotal, currency)}
            </span>
          </div>

          {shipping > 0 && (
            <div className="flex justify-between items-center gap-4 md:gap-8">
              <span className="text-sm font-medium text-text-primary dark:text-text-primary">
                {t("orders.shipping")}
              </span>
              <span className="text-sm font-semibold text-text-primary dark:text-text-primary">
                {formatPrice(shipping, currency)}
              </span>
            </div>
          )}

          {shipping === 0 && (
            <div className="flex justify-between items-center gap-4 md:gap-8">
              <span className="text-sm font-medium text-[#17b26a]">
                {t("orders.freeShipping")}
              </span>
              <span className="text-sm font-semibold text-[#17b26a]">
                {formatPrice(0, currency)}
              </span>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-[#e3e8ef] dark:bg-gray-700 my-2" />

        {/* Total */}
        <div className="flex justify-between items-center gap-4 md:gap-8">
          <span className="text-sm font-medium text-text-primary dark:text-text-primary">
            {t("orders.total")}
          </span>
          <span className="text-sm font-semibold text-text-primary dark:text-text-primary">
            {formatPrice(totalAmount, currency)}
          </span>
        </div>

        {/* Cashback */}
        {cashback > 0 && (
          <div className="flex items-center justify-between gap-4 md:gap-8 bg-[#f0fdf4] dark:bg-green-950/30 rounded-lg px-3 py-2 mt-1">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-[#17b26a] flex-shrink-0" />
              <span className="text-sm font-medium text-[#17b26a]">
                {t("orders.cashbackEarned")}
              </span>
            </div>
            <span className="text-sm font-bold text-[#17b26a]">
              +{formatCredit(cashback)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
